// src/features/foreign_exchange_rates/new.tsx

import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import {
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { createForeignExchangeCurrencyOptions } from "./config";

export function ForeignExchangeRatesEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  // All fields optional by default, except those required by the Ruby form:
  // code, name, currency_from, currency_to
  const FormSchema = useMemo(
    () =>
      z.object({
        code: z
          .string()
          .min(1, t("foreign_exchange_rate.validation.code_required")),
        name: z
          .string()
          .min(1, t("foreign_exchange_rate.validation.name_required")),
        description: z.string().nullable().optional(),
        currency_from: z.coerce.number(),
        currency_to: z.coerce.number(),
      }),
    [t],
  );

  // Function to adjust enum values from number to string for form display
  const adjustOnReset = (key: string, value: any) => {
    if (key === "currency_from" || key === "currency_to") {
      // Convert number to string for SelectField compatibility
      return value !== null && value !== undefined ? String(value) : value;
    }
    return value;
  };

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("foreign_exchange_rate.edit.title", { id })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="code"
                    label={t("foreign_exchange_rate.fields.code")}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="name"
                    label={t("foreign_exchange_rate.fields.name")}
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* Row 2 - full width */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="description"
                    label={t("foreign_exchange_rate.fields.description")}
                    control={form.control}
                    placeholder={t(
                      "foreign_exchange_rate.edit.description_placeholder",
                    )}
                  />
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="currency_from"
                    label={t("foreign_exchange_rate.fields.currency_from")}
                    options={createForeignExchangeCurrencyOptions()}
                    control={form.control}
                  />
                  <SelectField
                    name="currency_to"
                    label={t("foreign_exchange_rate.fields.currency_to")}
                    options={createForeignExchangeCurrencyOptions()}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirm New Item Modal */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("foreign_exchange_rate.edit.confirm_title")}
          >
            {changedFields
              ? Diff(changedFields, [])
              : t("foreign_exchange_rate.edit.no_changes")}
          </ConfirmationModal>

          {/* Cancel/New List Modal */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
